'use client'

import React, { useMemo, useState, useEffect, useRef } from 'react'
import { ClientSideRowModelModule } from '@ag-grid-community/client-side-row-model'
import {
  ColDef,  
  GridOptions,
  ModuleRegistry,  
} from '@ag-grid-community/core'
import { CsvExportModule } from '@ag-grid-community/csv-export'
import { AgGridReact, CustomLoadingOverlayProps } from '@ag-grid-community/react'
import { AdvancedFilterModule } from '@ag-grid-enterprise/advanced-filter'
import { ColumnsToolPanelModule } from '@ag-grid-enterprise/column-tool-panel'
import { LicenseManager } from '@ag-grid-enterprise/core'
import { MenuModule } from '@ag-grid-enterprise/menu'
import { MultiFilterModule } from '@ag-grid-enterprise/multi-filter'
import { SetFilterModule } from '@ag-grid-enterprise/set-filter'
import { SideBarModule } from '@ag-grid-enterprise/side-bar'

import { AGGWrapperContextProvider, ButtonV2, LoadingSpinner } from '@fsg/gui-bits'
import { cn } from '@app/utils'
import { AGGridWrapperProps } from '@gui/types'

if (typeof window !== 'undefined') {
  ModuleRegistry.registerModules([
    AdvancedFilterModule,
    ClientSideRowModelModule,
    MultiFilterModule,
    SetFilterModule,
    ColumnsToolPanelModule,
    CsvExportModule,
    MenuModule,
    SideBarModule,
  ])
}

export function AGGridCore<ContextType>({
  className,
  responsiveOptions,
  gridRef,
  header: customHeader,
  inlineAddRow,
  context = { gridRef: null },
  ...props
}: AGGridWrapperProps) {
  const onExportButton = React.useCallback(() => {
    gridRef.current!.api.exportDataAsCsv()
  }, [gridRef])

  const defaultColDef: ColDef = React.useMemo(
    () => ({
      autoHeight: true,
      sortable: true, // REVIEW >> Why is this not having an effect?
      resizable: true,
      // filter: 'agSetColumnFilter',
      editable: false,
      maxWidth: 325,
      // cellDataType: 'text',
      // * Filter tab contents and order determined by `menuTabs` array order.   The valid values are: 'filterMenuTab', 'generalMenuTab' and 'columnsMenuTab'.
      menuTabs: [
        'filterMenuTab',
        // 'generalMenuTab',
        'columnsMenuTab',
      ],
      
      suppressClickEdit: true,
      
    
      suppressKeyboardEvent: (params) => {
       
        if (!params.colDef.editable || params.editing) {
          return false;
        }
        
        const api = params.api as any;
        if (api.__allowDateEditing === true) {
          return false; 
        }
        
        if (params.event.key === 'Enter') {
          return false;
        }
        
        const navigationKeys = ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 
                              'Home', 'End', 'PageUp', 'PageDown', 'Tab', 'Escape'];
        if (navigationKeys.includes(params.event.key)) {
          return false; 
        }
        
        const isPrintableKey = params.event.key.length === 1;
        if (isPrintableKey || params.event.key === 'Backspace' || params.event.key === 'Delete') {
          return true; 
        }
        
        return false;
      },
      
      onCellDoubleClicked: (params) => {
        if (params.colDef.editable) {
          params.api.startEditingCell({
            rowIndex: params.rowIndex,
            colKey: params.column.getColId()
          });
        }
      },
      ...props.defaultColDef,
    }),
    [props.defaultColDef],
  )

  // https://www.ag-grid.com/javascript-data-grid/grid-options/
  const gridOptions: GridOptions = React.useMemo(
    () => ({
      paginationPageSize: 20,
      cacheBlockSize: 20,
      domLayout: 'normal',
      sideBar: {
        hiddenByDefault: true,
      },
      overlayNoRowsTemplate: '<span class="ag-overlay-loading-center">No row data to show</span>',
      overlayLoadingTemplate: '<span class="ag-overlay-loading-center">Loading...</span>',
      suppressMenuHide: true, // ? Always show column menu, not only on hover
      justifyOptions: {
        autoSizeColumnsOptions: {
          columnLimits: [{ key: 'icon', maxWidth: 50 }],
        },
      },
      rowSelection: 'single',
      suppressRowClickSelection: true,      
      onCellFocused: (event) => {
        if (event.rowIndex !== null && event.rowIndex >= 0) {
          event.api.deselectAll();
          const rowNode = event.api.getDisplayedRowAtIndex(event.rowIndex);
          if (rowNode) rowNode.setSelected(true, false);
        }
      },
      suppressCellFocus: false,
      ...props, // * columnDefs // * (ColDef<TData> | ColGroupDef<TData>)[] | null;
      // NOTE: Following cannot be overwritten on instantiation ---------
      pagination: true,
      animateRows: true,
      // ----------------------------------------------------------------

      defaultColDef, // * ColDef<TData> A default column definition. Items defined in the actual column definitions get precedence.
    }),
    [defaultColDef, props],
  )

  function renderHeader() {
    if (customHeader) {
      return customHeader
    } else {
      return (
        <>
          <ButtonV2 variant="secondary" size="lg" onClick={onExportButton}>
            Export CSV
          </ButtonV2>
        </>
      )
    }
  }

  const id = `agg-wrapper-${React.useId().replace(/:/g, '')}`

  const licenseKey =
    'Using_this_AG_Grid_Enterprise_key_( AG-042964 )_in_excess_of_the_licence_granted_is_not_permitted___Please_report_misuse_to_( legal@ag-grid.com )___For_help_with_changing_this_key_please_contact_( info@ag-grid.com )___( Facility Solutions Group, Inc )_is_granted_a_( Multiple Applications )_Developer_License_for_( 3 )_Front-End_JavaScript_developers___All_Front-End_JavaScript_developers_need_to_be_licensed_in_addition_to_the_ones_working_with_AG_Grid_Enterprise___This_key_has_not_been_granted_a_Deployment_License_Add-on___This_key_works_with_AG_Grid_Enterprise_versions_released_before_( 24 June 2024 )____[v2]_MTcxOTE4MzYwMDAwMA==add60d1200d6e860c439296c3d979842'
  LicenseManager.setLicenseKey(licenseKey)

  const loader = (props: CustomLoadingOverlayProps & { loadingMessage: string }) => {
    return (
      <div className="ag-overlay-loading-center" role="presentation">
        <LoadingSpinner />
        <div aria-live="polite" aria-atomic="true">
          {props.loadingMessage}
        </div>
      </div>
    )
  }

  const loadingOverlayComponent = React.useMemo(() => {
    return loader
  }, [])
  const loadingOverlayComponentParams = React.useMemo(() => {
    return {
      loadingMessage: '',
    }
  }, [])

  const memoizedContext = useMemo(() => {
    return context
  }, [context])

  const containerRef = React.useRef<HTMLDivElement>(null)
  const [containerHeight, setContainerHeight] = React.useState('75vh')

  React.useEffect(() => {
    const container = containerRef.current
    if (!container) return

    const updateHeight = () => {
      const windowHeight = window.innerHeight
      const containerTop = container.getBoundingClientRect().top
      const headerHeight = container.querySelector('.grid-header')?.getBoundingClientRect().height ?? 0
      const footerHeight = props.suppressPaginationPanel ? 0 : 48
      const availableHeight = windowHeight - containerTop - headerHeight - footerHeight
      setContainerHeight(`${availableHeight}px`)
    }

    const resizeObserver = new ResizeObserver(updateHeight)
    resizeObserver.observe(container)
    window.addEventListener('resize', updateHeight)
    updateHeight()

    return () => {
      resizeObserver.disconnect()
      window.removeEventListener('resize', updateHeight)
    }
  }, [props.suppressPaginationPanel])

  return (
    <AGGWrapperContextProvider gridRef={gridRef} value={memoizedContext}>
      {/* COMPONENT WRAPPER */}
      <div ref={containerRef} style={props.style} id={id} className={cn('ag-theme-quartz ag-theme-fsg rounded-lg border-off-white shadow', className)}>
        {/* HEADER */}
        <div className="w-full flex gap-2xl items-center rounded-tl-md rounded-tr-md bg-white p-sm grid-header">{renderHeader()}</div>
        {/* SIZING WRAPPER */}
        <div style={{ height: containerHeight }} className={props.gridComponentWrapper ?? ''}>
          <AgGridReact
            ref={gridRef}
            suppressServerSideFullWidthLoadingRow
            loadingOverlayComponent={loadingOverlayComponent}
            loadingOverlayComponentParams={loadingOverlayComponentParams}
            {...gridOptions}
            className="border-off-white "
          />
        </div>
        {/* FOOTER (IF NO PAGINATION PANEL) */}
        {props.suppressPaginationPanel ? (
          <div className="rounded-bl-md rounded-br-md min-h-min border border-t-0 border-solid border-gray-darker-200 bg-white p-sm shadow"></div>
        ) : null}
      </div>
    </AGGWrapperContextProvider>
  )
}
